import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, ArrowUpRight, Crown, Shield, Zap, TrendingUp, Trophy, Star, CheckCircle2,
  Info, X, Smartphone, Bitcoin, Building2, Globe, Lock, Banknote, Wallet, Sparkles,
  ChevronDown, Layers, Calendar, FileText, Award, Target,
  Briefcase, Users, ShieldCheck, Gauge,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import NeuralBrainBackground from '../components/NeuralBrainBackground';
import LivePayoutTicker from '../components/LivePayoutTicker';
import PayoutProofSlideshow from '../components/PayoutProofSlideshow';
import AmbientAurora from '../components/AmbientAurora';
import api from '../utils/api';

// ── Small utility components ──────────────────────────────────────────
const FadeUp = ({ children, delay = 0, y = 24, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const SectionLabel = ({ children, gold = false }) => (
  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${gold ? 'border-brand-gold/30 bg-brand-gold/5 text-brand-gold' : 'border-brand-primary/30 bg-brand-primary/5 text-brand-primary'} text-[10px] font-bold uppercase tracking-[0.2em]`}>
    <span className={`w-1.5 h-1.5 rounded-full ${gold ? 'bg-brand-gold' : 'bg-brand-primary'} animate-pulse`} />
    {children}
  </div>
);

const Glass = ({ className = '', children, gold = false, ...rest }) => (
  <div
    className={`relative rounded-2xl border ${gold ? 'border-brand-gold/15 bg-gradient-to-br from-brand-gold/[0.05] via-white/[0.02] to-transparent' : 'border-white/8 bg-white/[0.03]'} backdrop-blur-xl ${className}`}
    {...rest}
  >
    <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent rounded-t-2xl" />
    {children}
  </div>
);

const AnimatedNumber = ({ value, prefix = '', suffix = '', duration = 1.1 }) => {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: false, margin: '-30px' });
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const startTime = performance.now();
    const end = Number(value) || 0;
    const tick = (t) => {
      const p = Math.min((t - startTime) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(start + (end - start) * eased);
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [value, inView, duration]);
  return <span ref={ref} className="tabular-nums font-mono">{prefix}{Math.round(display).toLocaleString()}{suffix}</span>;
};

// ── 1. HERO ───────────────────────────────────────────────────────────
const Hero = ({ stats }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <section ref={ref} className="relative pt-24 md:pt-32 pb-12 md:pb-16 overflow-hidden">
      {/* Neural brain canvas + radial glows (decorative bg) */}
      <div className="pointer-events-none absolute inset-0">
        {/* radial cyan + gold halos sit BEHIND the canvas */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[900px] bg-brand-primary/8 rounded-full blur-[160px]" />
        <div className="absolute top-40 right-0 w-[500px] h-[500px] bg-brand-gold/6 rounded-full blur-[160px]" />
        {/* subtle grid */}
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)', backgroundSize: '64px 64px' }} />
      </div>
      {/* Brain canvas — needs pointer events for cursor reactivity, sits above blurs but below content */}
      <div className="absolute inset-0 z-0">
        <NeuralBrainBackground />
      </div>
      {/* Soft black vignette so text stays readable atop the brain */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,transparent_30%,rgba(0,0,0,0.55)_75%,#000_100%)] z-0" />

      <motion.div style={{ y: y1, opacity }} className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pointer-events-none">
        <div className="pointer-events-auto">
        <FadeUp>
          <div className="flex justify-center mb-5">
            <SectionLabel gold>The Ayronn Elite Desk · Funded Trader Program</SectionLabel>
          </div>
        </FadeUp>

        {/* Live stats badge */}
        {stats && (
          <FadeUp delay={0.02}>
            <div className="flex justify-center mb-5">
              <div className="inline-flex items-center gap-3 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-md border border-white/10" data-testid="hero-live-stats">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-brand-positive opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-positive" />
                </span>
                <span className="text-[11px] font-mono text-zinc-300">
                  <span className="text-white font-bold">{stats.funded_traders.toLocaleString()}</span> funded traders
                </span>
                <span className="w-px h-3 bg-white/15" />
                <span className="text-[11px] font-mono text-zinc-300">
                  <span className="text-brand-positive font-bold">${(stats.week_paid_usd / 1000).toFixed(0)}K</span> paid this week
                </span>
                <span className="w-px h-3 bg-white/15 hidden sm:block" />
                <span className="text-[11px] font-mono text-zinc-300 hidden sm:inline-flex items-center gap-1">
                  <Crown className="w-2.5 h-2.5 text-brand-gold" />
                  <span className="text-brand-gold font-bold">{stats.elite_traders}</span> Elite
                </span>
              </div>
            </div>
          </FadeUp>
        )}
        <FadeUp delay={0.05}>
          <h1 className="text-center text-[40px] sm:text-6xl md:text-7xl font-black tracking-[-0.02em] leading-[1.02] font-heading text-white">
            The Future Of
            <br />
            <span className="bg-gradient-to-r from-white via-brand-primary to-white bg-clip-text text-transparent">Prop Trading</span>
            <br />
            Starts Here.
          </h1>
        </FadeUp>

        <FadeUp delay={0.15}>
          <p className="mt-5 max-w-2xl mx-auto text-center text-base md:text-lg text-zinc-400 leading-relaxed">
            Pass the challenge, get funded, scale your capital and unlock access to the
            <span className="text-brand-gold font-semibold"> Ayronn Elite Desk</span> — where the top traders
            qualify for <span className="text-white font-semibold">100% profit splits</span> and monthly salary benefits.
          </p>
        </FadeUp>

        {/* CTAs */}
        <FadeUp delay={0.25}>
          <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/pricing" data-testid="hero-cta-start-challenge"
              className="group relative inline-flex items-center gap-2 px-7 py-3.5 bg-brand-primary text-black font-bold rounded-xl hover:scale-[1.02] hover:shadow-[0_0_40px_rgba(0,212,255,0.45)] transition-all duration-300">
              Start Challenge
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <a href="#elite-desk" data-testid="hero-cta-how-elite"
              className="inline-flex items-center gap-2 px-7 py-3.5 text-white font-semibold rounded-xl border border-white/15 hover:bg-white/5 hover:border-white/25 transition-all">
              How Elite Status Works <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </FadeUp>

        {/* USP banner */}
        <FadeUp delay={0.35}>
          <div className="mt-10 mx-auto max-w-5xl">
            <Glass gold className="p-1">
              <div className="rounded-[14px] bg-[#050505]/80 grid grid-cols-2 md:grid-cols-4 gap-px overflow-hidden">
                {[
                  { icon: Crown, label: '100% Profit Split', sub: 'Elite tier' },
                  { icon: Banknote, label: 'Monthly Salary Benefits', sub: 'Elite tier' },
                  { icon: Layers, label: 'Capital Scaling', sub: 'Up to 4× allocation' },
                  { icon: ShieldCheck, label: 'Priority Support', sub: 'Direct desk line' },
                ].map((b, i) => (
                  <div key={i} className="bg-[#050505] p-5 hover:bg-white/[0.02] transition-colors group">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-brand-gold/10 border border-brand-gold/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <b.icon className="w-5 h-5 text-brand-gold" strokeWidth={1.5} />
                      </div>
                      <div className="min-w-0">
                        <div className="text-white text-sm font-bold leading-tight">{b.label}</div>
                        <div className="text-zinc-500 text-[11px] mt-0.5">{b.sub}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Glass>
          </div>
        </FadeUp>

        {/* Floating dashboard mock */}
        <FadeUp delay={0.5}>
          <div className="mt-12 relative max-w-5xl mx-auto">
            <div className="absolute inset-x-10 -inset-y-6 bg-brand-primary/10 blur-[80px] rounded-full" />
            <Glass className="relative p-5 md:p-7">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-brand-positive animate-pulse" />
                  <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-400 font-bold">Live · Funded Trader Desk</span>
                </div>
                <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-brand-gold/10 border border-brand-gold/20">
                  <Crown className="w-3 h-3 text-brand-gold" />
                  <span className="text-[10px] font-bold text-brand-gold uppercase tracking-wider">Elite</span>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { l: 'Account Balance', v: '$112,840.20', c: 'text-white', sub: '+12.84% YTD' },
                  { l: 'Total Payouts', v: '$28,310', c: 'text-brand-positive', sub: 'Last: 2 days ago' },
                  { l: 'Monthly Salary', v: '$2,500', c: 'text-brand-gold', sub: 'Elite Tier' },
                  { l: 'Profit Split', v: '100%', c: 'text-brand-gold', sub: 'Active' },
                ].map((s, i) => (
                  <div key={i} className="p-4 rounded-xl bg-black/40 border border-white/5">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{s.l}</div>
                    <div className={`text-xl md:text-2xl font-black font-mono mt-1 ${s.c}`}>{s.v}</div>
                    <div className="text-[10px] text-zinc-500 mt-1">{s.sub}</div>
                  </div>
                ))}
              </div>

              {/* Mini chart bars */}
              <div className="mt-5 flex items-end gap-1.5 h-14">
                {[40, 55, 48, 62, 70, 58, 75, 82, 78, 88, 95, 92, 98, 100].map((h, i) => (
                  <motion.div
                    key={i}
                    initial={{ height: 0 }}
                    animate={{ height: `${h}%` }}
                    transition={{ delay: 0.6 + i * 0.04, duration: 0.6, ease: 'easeOut' }}
                    className={`flex-1 rounded-t ${i === 13 ? 'bg-brand-gold' : 'bg-brand-primary/40'}`}
                  />
                ))}
              </div>
            </Glass>

            {/* Floating side cards */}
            <motion.div
              initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.9 }}
              className="hidden lg:block absolute -right-12 top-1/3 w-[200px]"
            >
              <Glass className="p-3.5">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-zinc-400"><Trophy className="w-3 h-3 text-brand-gold" /> Top Trader</div>
                <div className="mt-1.5 text-white text-sm font-bold">Trader #AYR-2841</div>
                <div className="text-brand-positive text-base font-mono font-bold mt-1">+38.4%</div>
                <div className="text-[10px] text-zinc-500">30-day return</div>
              </Glass>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.05 }}
              className="hidden lg:block absolute -left-12 bottom-10 w-[200px]"
            >
              <Glass className="p-3.5">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-zinc-400"><Wallet className="w-3 h-3 text-brand-primary" /> Just Paid</div>
                <div className="mt-1.5 text-white text-sm font-bold">Priya M.</div>
                <div className="text-brand-primary text-base font-mono font-bold mt-1">₹2,84,500</div>
                <div className="text-[10px] text-zinc-500">Indian Bank · 12m ago</div>
              </Glass>
            </motion.div>
          </div>
        </FadeUp>
        </div>
      </motion.div>
    </section>
  );
};

// ── 2. TRUST STRIP ────────────────────────────────────────────────────
const TrustStrip = () => {
  const items = [
    { icon: Smartphone, label: 'UPI Payments' },
    { icon: Bitcoin, label: 'Crypto Payments' },
    { icon: Building2, label: 'Indian Bank Payouts' },
    { icon: Globe, label: 'Global Trader Access' },
    { icon: Zap, label: 'Fast Payout Processing' },
    { icon: Crown, label: 'Elite Desk Program' },
    { icon: FileText, label: 'Transparent Rules' },
    { icon: Lock, label: 'Secure Infrastructure' },
  ];
  const looped = [...items, ...items];
  return (
    <section className="relative py-10 border-y border-white/5 overflow-hidden bg-gradient-to-r from-black via-[#050505] to-black">
      <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-black to-transparent z-10 pointer-events-none" />
      <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-black to-transparent z-10 pointer-events-none" />
      <motion.div
        className="flex gap-12 whitespace-nowrap"
        animate={{ x: ['0%', '-50%'] }}
        transition={{ duration: 40, repeat: Infinity, ease: 'linear' }}
      >
        {looped.map((it, i) => (
          <div key={i} className="flex items-center gap-2.5 text-zinc-400 hover:text-white transition-colors">
            <it.icon className="w-4 h-4 text-brand-primary/70" strokeWidth={1.5} />
            <span className="text-sm font-semibold uppercase tracking-[0.15em]">{it.label}</span>
          </div>
        ))}
      </motion.div>
    </section>
  );
};

// ── 3. WHY CHOOSE AYRONN (Bento Grid) ─────────────────────────────────
const WhyChoose = () => {
  const features = [
    { icon: Crown, title: 'Elite Trader Salary Program', desc: 'Monthly salary benefits for qualified Elite Traders.', span: 'md:col-span-2', gold: true },
    { icon: TrendingUp, title: '100% Profit Split Potential', desc: 'Keep every dollar you earn at the Elite tier.' },
    { icon: Target, title: 'Professional Growth Path', desc: '4 tiers — Challenge → Funded → Pro → Elite.' },
    { icon: Layers, title: 'Capital Scaling', desc: 'Scale your allocation as you perform.' },
    { icon: ShieldCheck, title: 'Transparent Evaluation', desc: 'Clear, fair, rule-based progression.' },
    { icon: Smartphone, title: 'Multiple Payment Methods', desc: 'UPI · Crypto · Bank transfer supported.' },
    { icon: Zap, title: 'Fast Payout Experience', desc: 'Daily payouts to your preferred channel.' },
    { icon: Globe, title: 'Global Accessibility', desc: 'India-first. Built for traders worldwide.' },
  ];
  return (
    <section className="relative py-14 md:py-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <FadeUp className="text-center mb-10">
          <SectionLabel>Why Ayronn</SectionLabel>
          <h2 className="mt-5 text-4xl md:text-5xl font-black font-heading text-white tracking-[-0.02em]">
            Built for traders who want <br /><span className="text-brand-primary">more than just funding.</span>
          </h2>
        </FadeUp>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 hidden md:grid">
          {features.map((f, i) => (
            <FadeUp key={i} delay={i * 0.04} className={f.span || ''}>
              <Glass gold={f.gold} className={`p-6 h-full transition-all duration-300 hover:-translate-y-1 hover:border-white/20 group`}>
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${f.gold ? 'bg-brand-gold/10 border border-brand-gold/25' : 'bg-brand-primary/10 border border-brand-primary/20'}`}>
                  <f.icon className={`w-5 h-5 ${f.gold ? 'text-brand-gold' : 'text-brand-primary'}`} strokeWidth={1.5} />
                </div>
                <h3 className="text-white font-bold text-lg leading-tight">{f.title}</h3>
                <p className="mt-1.5 text-sm text-zinc-400 leading-relaxed">{f.desc}</p>
              </Glass>
            </FadeUp>
          ))}
        </div>

        {/* Mobile: horizontal snap-scroll — kills the "stacked boxes" feel */}
        <div className="md:hidden -mx-6 px-6 pb-2 overflow-x-auto snap-x snap-mandatory flex gap-3 hide-scrollbar" data-testid="why-choose-mobile-scroll">
          {features.map((f, i) => (
            <div key={i} className="snap-start flex-shrink-0 w-[75vw] max-w-[300px]">
              <Glass gold={f.gold} className="p-5 h-full">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 ${f.gold ? 'bg-brand-gold/10 border border-brand-gold/25' : 'bg-brand-primary/10 border border-brand-primary/20'}`}>
                  <f.icon className={`w-5 h-5 ${f.gold ? 'text-brand-gold' : 'text-brand-primary'}`} strokeWidth={1.5} />
                </div>
                <h3 className="text-white font-bold text-base leading-tight">{f.title}</h3>
                <p className="mt-1.5 text-[13px] text-zinc-400 leading-relaxed">{f.desc}</p>
              </Glass>
            </div>
          ))}
          <div className="flex-shrink-0 w-2" />
        </div>
        <div className="mt-3 flex items-center justify-center gap-1.5 md:hidden text-[10px] uppercase tracking-widest text-zinc-500">
          <span className="w-6 h-px bg-zinc-700" /> swipe <span className="w-6 h-px bg-zinc-700" />
        </div>
      </div>
    </section>
  );
};

// ── 4. TRADER JOURNEY ─────────────────────────────────────────────────
const TraderJourney = () => {
  const stages = [
    { name: 'Challenge Trader', desc: 'Pass the evaluation — show consistency, hit profit target.', icon: Target, accent: 'cyan' },
    { name: 'Funded Trader', desc: 'Trade with real capital. Start earning splits.', icon: Briefcase, accent: 'cyan' },
    { name: 'Pro Trader', desc: 'Scale your account and unlock higher splits.', icon: TrendingUp, accent: 'cyan' },
    { name: 'Elite Trader', desc: 'Join the Elite Desk. 100% split + monthly salary.', icon: Crown, accent: 'gold' },
  ];
  return (
    <section className="relative py-14 md:py-20 bg-gradient-to-b from-black via-[#030303] to-black">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <FadeUp className="text-center mb-16">
          <SectionLabel>The Path</SectionLabel>
          <h2 className="mt-5 text-4xl md:text-5xl font-black font-heading text-white tracking-[-0.02em]">
            Your journey to becoming<br />
            <span className="bg-gradient-to-r from-brand-primary via-brand-primary to-brand-gold bg-clip-text text-transparent">an Elite Trader.</span>
          </h2>
        </FadeUp>

        <div className="relative">
          {/* connecting line (desktop) */}
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-brand-primary via-brand-primary to-brand-gold opacity-30" />

          {stages.map((s, i) => (
            <FadeUp key={i} delay={i * 0.08}>
              <div className={`relative flex flex-col md:flex-row items-center gap-6 mb-10 ${i % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
                <div className="md:w-1/2 md:px-10">
                  <Glass gold={s.accent === 'gold'} className="p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`text-[10px] uppercase tracking-[0.2em] font-bold ${s.accent === 'gold' ? 'text-brand-gold' : 'text-brand-primary'}`}>Stage {i + 1}</span>
                      {s.accent === 'gold' && <Sparkles className="w-3.5 h-3.5 text-brand-gold" />}
                    </div>
                    <h3 className={`text-2xl font-bold ${s.accent === 'gold' ? 'text-brand-gold' : 'text-white'}`}>{s.name}</h3>
                    <p className="text-sm text-zinc-400 mt-1.5">{s.desc}</p>
                  </Glass>
                </div>
                {/* node */}
                <div className="relative z-10 flex-shrink-0">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center ${s.accent === 'gold' ? 'bg-brand-gold/15 border-2 border-brand-gold shadow-[0_0_40px_rgba(212,175,55,0.35)]' : 'bg-brand-primary/15 border-2 border-brand-primary shadow-[0_0_30px_rgba(0,212,255,0.25)]'}`}>
                    <s.icon className={`w-6 h-6 ${s.accent === 'gold' ? 'text-brand-gold' : 'text-brand-primary'}`} strokeWidth={1.5} />
                  </div>
                </div>
                <div className="md:w-1/2" />
              </div>
            </FadeUp>
          ))}

          {/* Final reward badge */}
          <FadeUp delay={0.4}>
            <div className="relative flex justify-center mt-4">
              <div className="relative">
                <div className="absolute inset-0 bg-brand-gold/20 rounded-2xl blur-2xl" />
                <Glass gold className="relative px-8 py-6 text-center">
                  <Crown className="w-7 h-7 text-brand-gold mx-auto mb-2" />
                  <div className="text-brand-gold font-black text-xl md:text-2xl">100% Profit Split + Monthly Salary</div>
                  <div className="text-xs text-zinc-400 mt-1.5">The Elite reward · eligibility & terms apply</div>
                </Glass>
              </div>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};

// ── 5. CHALLENGE PROGRAMS ─────────────────────────────────────────────
const EliteSalaryInfo = ({ open, onClose }) => (
  <AnimatePresence>
    {open && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-md" onClick={(e) => e.stopPropagation()}
        >
          <Glass gold className="p-6">
            <button onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-lg text-zinc-400 hover:bg-white/10 hover:text-white" data-testid="elite-salary-info-close">
              <X className="w-4 h-4" />
            </button>
            <div className="w-12 h-12 rounded-xl bg-brand-gold/15 border border-brand-gold/30 flex items-center justify-center mb-4">
              <Crown className="w-6 h-6 text-brand-gold" />
            </div>
            <h3 className="text-xl font-black text-white">Elite Trader Salary Program</h3>
            <p className="mt-3 text-sm text-zinc-300 leading-relaxed">
              Monthly salary benefits are reserved for <span className="text-brand-gold font-semibold">Elite Traders</span> — those who complete <span className="text-brand-gold font-bold">12 consecutive successful payouts from a single funded account</span>.
            </p>
            <ul className="mt-4 space-y-2 text-sm text-zinc-400">
              <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-brand-gold mt-0.5 flex-shrink-0" /> <span><strong className="text-white">12 paid payouts</strong> from the same account.</span></li>
              <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-brand-gold mt-0.5 flex-shrink-0" /> Account must stay in good standing (no breach).</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-brand-gold mt-0.5 flex-shrink-0" /> Salary tier matches the account size.</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-brand-gold mt-0.5 flex-shrink-0" /> Consistency framework must be maintained.</li>
            </ul>
            <p className="mt-4 text-[10px] text-zinc-600 leading-relaxed">
              AYRONN is an evaluation &amp; performance-reward platform. The Elite Salary is a reward, not a guaranteed return. Eligibility &amp; payment subject to internal review.
            </p>
            <a href="#elite-desk" onClick={onClose} className="mt-5 inline-flex items-center gap-2 text-brand-gold text-sm font-bold hover:underline" data-testid="elite-salary-info-learn">
              Learn how Elite status works <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </Glass>
        </motion.div>
      </div>
    )}
  </AnimatePresence>
);

const ChallengePrograms = ({ plans }) => {
  const [infoOpen, setInfoOpen] = useState(false);
  const [category, setCategory] = useState('1-step');
  const filtered = plans.filter(p => p.challenge_type === category);

  const categories = [
    { id: '1-step', label: '1-Step' },
    { id: '2-step', label: '2-Step' },
    { id: 'instant', label: 'Instant' },
  ];

  return (
    <section id="programs" className="relative py-14 md:py-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <FadeUp className="text-center mb-10">
          <SectionLabel>Challenge Programs</SectionLabel>
          <h2 className="mt-5 text-4xl md:text-5xl font-black font-heading text-white tracking-[-0.02em]">
            Pick a program. <span className="text-brand-primary">Start your path.</span>
          </h2>
        </FadeUp>

        {/* category tabs */}
        <FadeUp className="flex justify-center mb-10">
          <div className="inline-flex p-1 rounded-xl bg-white/[0.03] border border-white/10">
            {categories.map(c => (
              <button key={c.id} onClick={() => setCategory(c.id)} data-testid={`program-tab-${c.id}`}
                className={`px-5 py-2 text-sm font-bold rounded-lg transition-all ${category === c.id ? 'bg-brand-primary text-black' : 'text-zinc-400 hover:text-white'}`}>
                {c.label}
              </button>
            ))}
          </div>
        </FadeUp>

        {/* Desktop grid */}
        <div className="hidden md:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {filtered.map((p, i) => {
            const popular = p.account_size === 25000;
            return (
              <FadeUp key={p.id} delay={i * 0.04}>
                <Glass className={`p-5 h-full relative flex flex-col ${popular ? 'border-brand-primary/40 ring-1 ring-brand-primary/30 shadow-[0_0_40px_rgba(0,212,255,0.15)]' : ''} hover:-translate-y-1 transition-all duration-300`}>
                  {popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-brand-primary text-black text-[10px] font-black uppercase tracking-wider rounded-full">Most Popular</div>
                  )}
                  <div className="text-zinc-500 text-[10px] uppercase font-bold tracking-wider">Account</div>
                  <div className="text-3xl font-black text-white font-mono">${(p.account_size / 1000).toFixed(0)}K</div>
                  <div className="mt-3 mb-4">
                    <div className="text-[10px] text-zinc-500 uppercase">Challenge Fee</div>
                    <div className="text-brand-primary text-xl font-bold font-mono">${p.price}</div>
                  </div>
                  <div className="space-y-2 text-xs flex-1">
                    <div className="flex justify-between"><span className="text-zinc-500">Profit Target</span><span className="text-white font-mono">{p.profit_target || 0}%</span></div>
                    <div className="flex justify-between"><span className="text-zinc-500">Max Drawdown</span><span className="text-white font-mono">{p.max_drawdown}%</span></div>
                    <div className="flex justify-between"><span className="text-zinc-500">Profit Split</span><span className="text-white font-mono">{p.profit_split}%</span></div>
                    <div className="flex items-center justify-between pt-2 mt-2 border-t border-brand-gold/15">
                      <span className="flex items-center gap-1 text-brand-gold text-[11px] font-bold uppercase tracking-wider">
                        Elite Salary*
                        <button onClick={() => setInfoOpen(true)} className="p-0.5 rounded hover:bg-brand-gold/10" data-testid={`elite-info-${p.id}`}>
                          <Info className="w-3 h-3" />
                        </button>
                      </span>
                      <span className="text-brand-gold text-sm font-black font-mono">
                        ${(p.elite_salary_potential || 0).toLocaleString()}<span className="text-[9px] text-brand-gold/70 font-bold">/mo</span>
                      </span>
                    </div>
                    <div className="text-[9px] text-zinc-600 leading-snug">After {p.elite_payouts_required || 12} consistent payouts from this account.</div>
                  </div>
                  <Link to={`/checkout/${p.id}`} data-testid={`program-cta-${p.id}`}
                    className={`mt-5 w-full inline-flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-bold transition-all ${popular ? 'bg-brand-primary text-black hover:shadow-[0_0_25px_rgba(0,212,255,0.5)]' : 'bg-white/5 border border-white/10 text-white hover:bg-white/10'}`}>
                    Start <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </Glass>
              </FadeUp>
            );
          })}
        </div>

        {/* Mobile: full-bleed horizontal snap carousel */}
        <div className="md:hidden -mx-6 px-6 overflow-x-auto snap-x snap-mandatory flex gap-3 pb-3 hide-scrollbar" data-testid="programs-mobile-scroll">
          {filtered.map((p) => {
            const popular = p.account_size === 25000;
            return (
              <div key={p.id} className="snap-start flex-shrink-0 w-[82vw] max-w-[340px]">
                <Glass className={`p-5 h-full relative flex flex-col ${popular ? 'border-brand-primary/40 ring-1 ring-brand-primary/30 shadow-[0_0_40px_rgba(0,212,255,0.15)]' : ''}`}>
                  {popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-brand-primary text-black text-[10px] font-black uppercase tracking-wider rounded-full whitespace-nowrap">Most Popular</div>
                  )}
                  <div className="flex items-baseline justify-between">
                    <div>
                      <div className="text-zinc-500 text-[10px] uppercase font-bold tracking-wider">Account</div>
                      <div className="text-3xl font-black text-white font-mono">${(p.account_size / 1000).toFixed(0)}K</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] text-zinc-500 uppercase">Fee</div>
                      <div className="text-brand-primary text-xl font-bold font-mono">${p.price}</div>
                    </div>
                  </div>
                  <div className="mt-4 space-y-2 text-xs flex-1">
                    <div className="flex justify-between"><span className="text-zinc-500">Profit Target</span><span className="text-white font-mono">{p.profit_target || 0}%</span></div>
                    <div className="flex justify-between"><span className="text-zinc-500">Max Drawdown</span><span className="text-white font-mono">{p.max_drawdown}%</span></div>
                    <div className="flex justify-between"><span className="text-zinc-500">Profit Split</span><span className="text-white font-mono">{p.profit_split}%</span></div>
                    <div className="flex items-center justify-between pt-2 mt-2 border-t border-brand-gold/15">
                      <span className="flex items-center gap-1 text-brand-gold text-[11px] font-bold uppercase tracking-wider">
                        Elite Salary*
                        <button onClick={() => setInfoOpen(true)} className="p-0.5 rounded hover:bg-brand-gold/10" data-testid={`elite-info-m-${p.id}`}>
                          <Info className="w-3 h-3" />
                        </button>
                      </span>
                      <span className="text-brand-gold text-sm font-black font-mono">
                        ${(p.elite_salary_potential || 0).toLocaleString()}<span className="text-[9px] text-brand-gold/70 font-bold">/mo</span>
                      </span>
                    </div>
                  </div>
                  <Link to={`/checkout/${p.id}`} data-testid={`program-cta-m-${p.id}`}
                    className={`mt-4 w-full inline-flex items-center justify-center gap-1.5 py-3 rounded-lg text-sm font-bold transition-all ${popular ? 'bg-brand-primary text-black' : 'bg-white/5 border border-white/10 text-white'}`}>
                    Start Challenge <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </Glass>
              </div>
            );
          })}
          <div className="flex-shrink-0 w-2" />
        </div>
        <div className="mt-3 flex items-center justify-center gap-1.5 md:hidden text-[10px] uppercase tracking-widest text-zinc-500">
          <span className="w-6 h-px bg-zinc-700" /> swipe programs <span className="w-6 h-px bg-zinc-700" />
        </div>
      </div>
      <EliteSalaryInfo open={infoOpen} onClose={() => setInfoOpen(false)} />
    </section>
  );
};

// ── 6. ELITE DESK ─────────────────────────────────────────────────────
const EliteDesk = () => {
  const benefits = [
    { icon: Crown, title: '100% Profit Split Potential', desc: 'Keep every dollar of profit you generate.' },
    { icon: Banknote, title: 'Monthly Salary Benefits', desc: 'Regular monthly retainer for top performers.' },
    { icon: Layers, title: 'Higher Capital Allocation', desc: 'Scale to substantially larger account sizes.' },
    { icon: ShieldCheck, title: 'Priority Support', desc: 'Dedicated desk line — sub-hour response.' },
    { icon: Award, title: 'Elite Recognition', desc: 'Public leaderboard + verified Elite badge.' },
    { icon: TrendingUp, title: 'Accelerated Scaling', desc: 'Faster account upgrades on milestones.' },
  ];

  return (
    <section id="elite-desk" className="relative py-14 md:py-20 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[600px] bg-brand-gold/8 rounded-full blur-[160px]" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <FadeUp>
            <SectionLabel gold>The Inner Circle</SectionLabel>
            <h2 className="mt-5 text-4xl md:text-5xl font-black font-heading text-white tracking-[-0.02em]">
              The <span className="text-brand-gold">Ayronn Elite Desk</span>
            </h2>
            <p className="mt-5 text-base text-zinc-400 leading-relaxed max-w-lg">
              The highest-performing traders qualify for Elite status and unlock exclusive benefits — a category of compensation reserved for the top fraction of our funded community.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link to="/pricing" data-testid="elite-desk-cta"
                className="inline-flex items-center gap-2 px-6 py-3 bg-brand-gold text-black font-bold rounded-xl hover:shadow-[0_0_30px_rgba(212,175,55,0.45)] transition-all">
                Begin Your Journey <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#calculator" className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-1">
                Calculate potential earnings <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </FadeUp>

          <FadeUp delay={0.2}>
            <Glass gold className="p-1">
              <div className="rounded-[14px] bg-[#0A0805] p-6">
                <div className="flex items-center justify-between pb-4 mb-4 border-b border-brand-gold/10">
                  <div className="flex items-center gap-2">
                    <Crown className="w-5 h-5 text-brand-gold" />
                    <span className="text-brand-gold font-black uppercase tracking-wider text-sm">Elite Tier Benefits</span>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-zinc-500">Invitation Based</span>
                </div>
                <div className="space-y-3">
                  {benefits.map((b, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-lg hover:bg-brand-gold/[0.04] transition-colors">
                      <div className="w-9 h-9 rounded-lg bg-brand-gold/10 border border-brand-gold/20 flex items-center justify-center flex-shrink-0">
                        <b.icon className="w-4 h-4 text-brand-gold" strokeWidth={1.5} />
                      </div>
                      <div className="min-w-0">
                        <div className="text-white text-sm font-bold leading-tight">{b.title}</div>
                        <div className="text-zinc-500 text-xs mt-0.5">{b.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Glass>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};

// ── 7. ELITE EARNINGS CALCULATOR ──────────────────────────────────────
// Ties directly to backend PLANS_DATA + ELITE_SALARY_BY_SIZE.
// Discrete account sizes only (5K/10K/25K/50K/100K) — no invalid combinations.
const EliteCalculator = ({ plans = [] }) => {
  // Derive discrete sizes from active plans, or fallback to canonical list matching backend
  const canonicalSizes = [5000, 10000, 25000, 50000, 100000];
  const availableSizes = React.useMemo(() => {
    if (!plans.length) return canonicalSizes;
    const s = Array.from(new Set(plans.map(p => p.account_size))).sort((a, b) => a - b);
    return s.length ? s : canonicalSizes;
  }, [plans]);

  // Elite salary from backend (ELITE_SALARY_BY_SIZE)
  const eliteSalaryBySize = { 5000: 250, 10000: 500, 25000: 1000, 50000: 1800, 100000: 2500 };

  const [size, setSize] = useState(25000);
  const [returnPct, setReturnPct] = useState(6);
  const [status, setStatus] = useState('funded');

  useEffect(() => {
    // Ensure selected size is always in available list
    if (!availableSizes.includes(size)) setSize(availableSizes[Math.floor(availableSizes.length / 2)]);
  }, [availableSizes, size]);

  // Match backend elite salary — fetched from plans if present (respecting admin overrides)
  const planForSize = plans.find(p => p.account_size === size);
  const salaryFromPlan = planForSize?.elite_salary_potential;
  const eliteSalary = salaryFromPlan ?? eliteSalaryBySize[size] ?? 0;

  const split = status === 'elite' ? 100 : status === 'pro' ? 90 : 80;
  const salary = status === 'elite' ? eliteSalary : 0;
  const generated = Math.round((size * returnPct) / 100);
  const share = Math.round((generated * split) / 100);
  const total = share + salary;

  const fmtSize = (n) => (n >= 1000 ? `$${(n / 1000).toFixed(0)}K` : `$${n}`);

  return (
    <section id="calculator" className="relative py-14 md:py-20 bg-gradient-to-b from-black via-[#030303] to-black">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <FadeUp className="text-center mb-12">
          <SectionLabel gold>Earnings Calculator</SectionLabel>
          <h2 className="mt-5 text-4xl md:text-5xl font-black font-heading text-white tracking-[-0.02em]">
            Calculate what an <span className="text-brand-gold">Elite Trader</span> could earn.
          </h2>
          <p className="mt-4 text-sm text-zinc-500 max-w-xl mx-auto">
            Numbers tied to your real Ayronn plan sizes and Elite Desk salaries — no inflated math.
          </p>
        </FadeUp>

        <Glass className="p-6 md:p-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Inputs */}
            <div className="space-y-7">
              {/* Account Size — discrete buttons */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-[11px] uppercase tracking-wider text-zinc-400 font-bold">Account Size</label>
                  <span className="text-brand-primary font-mono font-bold">{fmtSize(size)}</span>
                </div>
                <div className="grid grid-cols-5 gap-1.5" data-testid="calc-size-group">
                  {availableSizes.map(s => (
                    <button
                      key={s}
                      onClick={() => setSize(s)}
                      data-testid={`calc-size-${s}`}
                      className={`px-2 py-2.5 rounded-lg text-[11px] md:text-xs font-black font-mono transition-all ${
                        size === s
                          ? 'bg-brand-primary text-black shadow-[0_0_18px_rgba(0,212,255,0.35)]'
                          : 'bg-white/[0.03] border border-white/10 text-zinc-400 hover:text-white hover:bg-white/[0.05]'
                      }`}
                    >
                      {fmtSize(s)}
                    </button>
                  ))}
                </div>
                <div className="mt-2 text-[10px] text-zinc-600">Only sizes we actually fund. No fake tiers.</div>
              </div>

              {/* Monthly Return — capped at realistic 15% */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-[11px] uppercase tracking-wider text-zinc-400 font-bold">Assumed Monthly Return %</label>
                  <span className="text-brand-primary font-mono font-bold">{returnPct}%</span>
                </div>
                <input type="range" min="1" max="15" step="0.5" value={returnPct}
                  onChange={(e) => setReturnPct(Number(e.target.value))}
                  className="w-full accent-brand-primary"
                  data-testid="calc-return" />
                <div className="flex justify-between text-[10px] text-zinc-600 mt-1.5"><span>1%</span><span>Elite avg: 6-9%</span><span>15%</span></div>
              </div>

              {/* Trader status */}
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-zinc-400 font-bold mb-3">Trader Status</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 'funded', label: 'Funded', sub: '80% split' },
                    { id: 'pro', label: 'Pro', sub: '90% split' },
                    { id: 'elite', label: 'Elite', sub: '100% + Salary' },
                  ].map(s => (
                    <button key={s.id} onClick={() => setStatus(s.id)} data-testid={`calc-status-${s.id}`}
                      className={`p-3 rounded-lg text-left transition-all ${status === s.id ? (s.id === 'elite' ? 'bg-brand-gold/15 border border-brand-gold/40 text-brand-gold' : 'bg-brand-primary/15 border border-brand-primary/40 text-brand-primary') : 'bg-white/[0.03] border border-white/10 text-zinc-400 hover:text-white'}`}>
                      <div className="text-xs font-black uppercase">{s.label}</div>
                      <div className="text-[10px] mt-0.5 opacity-80">{s.sub}</div>
                    </button>
                  ))}
                </div>
                {status === 'elite' && (
                  <div className="mt-3 text-[10.5px] text-brand-gold/80 flex items-start gap-1.5">
                    <Crown className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>Elite unlocks after 12 consecutive successful payouts. Salary is fixed by account size.</span>
                  </div>
                )}
              </div>
            </div>

            {/* Results */}
            <div className="space-y-4">
              {[
                { label: 'Generated Profit', value: generated, color: 'text-white', testid: 'calc-generated' },
                { label: `Profit Share (${split}%)`, value: share, color: 'text-brand-primary', testid: 'calc-share' },
                { label: `Elite Salary${size ? ` · ${fmtSize(size)}` : ''}`, value: salary, color: 'text-brand-gold', conditional: status !== 'elite', testid: 'calc-salary' },
              ].map((r, i) => (
                <div key={i} data-testid={r.testid} className={`p-5 rounded-xl bg-black/40 border border-white/5 ${r.conditional ? 'opacity-50' : ''}`}>
                  <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-bold">{r.label}</div>
                  <div className={`text-3xl font-black font-mono mt-1 ${r.color}`}>
                    <AnimatedNumber value={r.value} prefix="$" />
                  </div>
                </div>
              ))}
              <Glass gold className="p-5" data-testid="calc-total">
                <div className="text-[11px] uppercase tracking-wider text-brand-gold font-bold">Total Monthly Earnings</div>
                <div className="text-4xl md:text-5xl font-black font-mono mt-1 bg-gradient-to-r from-brand-gold to-brand-gold-soft bg-clip-text text-transparent">
                  <AnimatedNumber value={total} prefix="$" />
                </div>
                <div className="text-[10px] text-zinc-500 mt-2">Illustrative example. Actual profit depends on your trading. Elite salary values are fixed per plan.</div>
              </Glass>
            </div>
          </div>
        </Glass>
      </div>
    </section>
  );
};

// ── 8. RULE TRANSPARENCY ──────────────────────────────────────────────
const RuleModal = ({ rule, onClose }) => (
  <AnimatePresence>
    {rule && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-lg" onClick={(e) => e.stopPropagation()}
        >
          <Glass className="p-6">
            <button onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-lg text-zinc-400 hover:bg-white/10 hover:text-white" data-testid="rule-modal-close">
              <X className="w-4 h-4" />
            </button>
            <div className="w-12 h-12 rounded-xl bg-brand-primary/10 border border-brand-primary/25 flex items-center justify-center mb-4">
              <rule.icon className="w-6 h-6 text-brand-primary" strokeWidth={1.5} />
            </div>
            <h3 className="text-xl font-black text-white">{rule.title}</h3>
            <p className="mt-3 text-sm text-zinc-300 leading-relaxed whitespace-pre-line">{rule.details}</p>
            <Link to="/rules" onClick={onClose} className="mt-5 inline-flex items-center gap-2 text-brand-primary text-sm font-bold hover:underline">
              See full rulebook <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </Glass>
        </motion.div>
      </div>
    )}
  </AnimatePresence>
);

const RuleTransparency = () => {
  const [activeRule, setActiveRule] = useState(null);
  const rules = [
    { id: 'consistency', icon: Gauge, title: 'Consistency Framework', short: 'Steady, repeatable performance — not single-day spikes.', details: 'Our consistency framework ensures that funded traders demonstrate professional risk management.\n\n• No single trading day should exceed 30% of total profit (2-Step & Instant).\n• Floating losses are monitored to maintain risk discipline.\n• Triggering consistency does NOT breach the account — it pauses payouts pending review.' },
    { id: 'drawdown', icon: Shield, title: 'Maximum Drawdown', short: 'Clearly defined daily and total drawdown limits.', details: 'Max drawdown protects your account and reflects professional risk practices.\n\n• Daily Loss Limit: 3% (1-Step / 2-Step) or 4% (Instant).\n• Maximum Drawdown: 6% (1-Step / 2-Step) or 7% (Instant).\n• Limits are calculated against the initial account balance.' },
    { id: 'target', icon: Target, title: 'Profit Target', short: 'Realistic targets across all challenge types.', details: 'Profit targets are calibrated to demonstrate skill without forcing reckless risk.\n\n• 1-Step: 9% target.\n• 2-Step: 6% per phase.\n• Instant: No target — performance-based scaling.' },
    { id: 'payout', icon: Calendar, title: 'Payout Schedule', short: 'Fast, predictable payouts.', details: 'Funded traders enjoy daily payout eligibility.\n\n• Daily payouts once account is funded.\n• USDT TRC20, Indian Bank Transfer, or UPI options.\n• Minimum payout: $10. Typical processing within 24h of approval.' },
    { id: 'restrict', icon: AlertIcon, title: 'Trading Restrictions', short: 'A few clear restrictions to protect everyone.', details: 'AYRONN does not allow:\n\n• High-Frequency Trading (HFT) or latency-sensitive strategies.\n• Hedging or reverse trading across multiple accounts.\n• Account sharing — only the registered holder may trade.\n• Opening or closing trades within ±30 minutes of high-impact news events.' },
  ];

  return (
    <section className="relative py-14 md:py-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <FadeUp className="text-center mb-12">
          <SectionLabel>Transparency</SectionLabel>
          <h2 className="mt-5 text-4xl md:text-5xl font-black font-heading text-white tracking-[-0.02em]">
            Clear rules. <span className="text-brand-primary">No guesswork.</span>
          </h2>
        </FadeUp>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rules.map((r, i) => (
            <FadeUp key={r.id} delay={i * 0.05}>
              <Glass className="p-5 h-full flex flex-col">
                <div className="w-10 h-10 rounded-lg bg-brand-primary/10 border border-brand-primary/20 flex items-center justify-center mb-4">
                  <r.icon className="w-4 h-4 text-brand-primary" strokeWidth={1.5} />
                </div>
                <h3 className="text-white font-bold text-base">{r.title}</h3>
                <p className="mt-1 text-xs text-zinc-400 flex-1">{r.short}</p>
                <button onClick={() => setActiveRule(r)} data-testid={`rule-view-${r.id}`}
                  className="mt-4 inline-flex items-center gap-1.5 text-brand-primary text-xs font-bold hover:underline w-fit">
                  View Details <ArrowUpRight className="w-3 h-3" />
                </button>
              </Glass>
            </FadeUp>
          ))}
        </div>
      </div>
      <RuleModal rule={activeRule} onClose={() => setActiveRule(null)} />
    </section>
  );
};

const AlertIcon = (props) => <Shield {...props} />;

// ── 9. PAYMENTS & PAYOUTS ─────────────────────────────────────────────
const PaymentsAndPayouts = () => (
  <section className="relative py-14 md:py-20 bg-gradient-to-b from-black via-[#030303] to-black">
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <FadeUp className="text-center mb-14">
        <SectionLabel>Payments & Payouts</SectionLabel>
        <h2 className="mt-5 text-4xl md:text-5xl font-black font-heading text-white tracking-[-0.02em]">
          Built for <span className="text-brand-primary">Indian</span> and <span className="text-brand-gold">global</span> traders.
        </h2>
      </FadeUp>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FadeUp>
          <Glass className="p-7">
            <div className="flex items-center gap-2 mb-5">
              <span className="text-[11px] uppercase tracking-[0.2em] text-brand-primary font-bold">Payment Methods</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[
                { icon: Smartphone, label: 'UPI', sub: 'India only' },
                { icon: Bitcoin, label: 'Crypto', sub: 'BTC · USDT · ETH' },
                { icon: Building2, label: 'Bank Transfer', sub: 'Global' },
              ].map((p, i) => (
                <div key={i} className="p-4 rounded-xl bg-black/40 border border-white/5 hover:border-white/15 transition-colors text-center">
                  <p.icon className="w-6 h-6 text-brand-primary mx-auto mb-2" strokeWidth={1.5} />
                  <div className="text-white text-xs font-bold">{p.label}</div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">{p.sub}</div>
                </div>
              ))}
            </div>
          </Glass>
        </FadeUp>
        <FadeUp delay={0.1}>
          <Glass gold className="p-7">
            <div className="flex items-center gap-2 mb-5">
              <span className="text-[11px] uppercase tracking-[0.2em] text-brand-gold font-bold">Payout Methods</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: Building2, label: 'Indian Bank Account' },
                { icon: Smartphone, label: 'UPI Support' },
                { icon: Bitcoin, label: 'Crypto Withdrawal' },
                { icon: Globe, label: 'International Transfer' },
              ].map((p, i) => (
                <div key={i} className="p-4 rounded-xl bg-black/40 border border-white/5 hover:border-brand-gold/30 transition-colors flex items-center gap-3">
                  <p.icon className="w-5 h-5 text-brand-gold flex-shrink-0" strokeWidth={1.5} />
                  <div className="text-white text-xs font-bold">{p.label}</div>
                </div>
              ))}
            </div>
          </Glass>
        </FadeUp>
      </div>
    </div>
  </section>
);

// ── 10. SOCIAL PROOF ──────────────────────────────────────────────────
const SocialProof = () => {
  const testimonials = [
    { name: 'Rohan S.', city: 'Mumbai', status: 'Funded', amount: '$50K', text: 'Passed my 2-step in 11 days. UPI checkout was instant and payouts hit my SBI account in under 24 hours. Best Indian-friendly prop firm.', return: '+18.4%' },
    { name: 'Aisha K.', city: 'Bangalore', status: 'Elite', amount: '$100K', text: 'The Elite Desk is the real deal. Monthly salary + 100% split — Ayronn actually pays out, no excuses. I left two other firms for this.', return: '+24.1%' },
    { name: 'Daniel W.', city: 'London', status: 'Pro', amount: '$50K', text: 'Clean rules, fair drawdown, and the dashboard is best-in-class. Got my Pro upgrade after 3 months of consistent performance.', return: '+15.7%' },
    { name: 'Priya M.', city: 'Pune', status: 'Funded', amount: '$25K', text: 'Indian UPI deposit + Indian bank payout — I never worried about FX or international fees. Easy 5 stars.', return: '+12.3%' },
    { name: 'Carlos V.', city: 'Madrid', status: 'Elite', amount: '$200K', text: 'After 6 months I qualified for Elite. The monthly salary changed how I plan my trading life. This isn’t just funding — it’s a career.', return: '+31.0%' },
  ];
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((p) => (p + 1) % testimonials.length), 6000);
    return () => clearInterval(t);
  }, [testimonials.length]);
  const t = testimonials[i];
  return (
    <section className="relative py-14 md:py-20">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <FadeUp className="text-center mb-12">
          <SectionLabel>Trader Stories</SectionLabel>
          <h2 className="mt-5 text-4xl md:text-5xl font-black font-heading text-white tracking-[-0.02em]">
            Verified performance. <span className="text-brand-primary">Real payouts.</span>
          </h2>
        </FadeUp>

        <Glass className="p-8 md:p-10">
          <AnimatePresence mode="wait">
            <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4 }}>
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, k) => <Star key={k} className="w-4 h-4 text-brand-gold fill-brand-gold" />)}
              </div>
              <p className="text-lg md:text-xl text-zinc-200 leading-relaxed">“{t.text}”</p>
              <div className="mt-6 flex items-center justify-between flex-wrap gap-4">
                <div>
                  <div className="text-white font-bold">{t.name}</div>
                  <div className="text-xs text-zinc-500">{t.city} · <span className={`font-bold ${t.status === 'Elite' ? 'text-brand-gold' : 'text-brand-primary'}`}>{t.status} Trader</span></div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right"><div className="text-[10px] text-zinc-500 uppercase">Account</div><div className="text-white font-bold font-mono">{t.amount}</div></div>
                  <div className="text-right"><div className="text-[10px] text-zinc-500 uppercase">Return</div><div className="text-brand-positive font-bold font-mono">{t.return}</div></div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
          <div className="mt-6 flex justify-center gap-1.5">
            {testimonials.map((_, k) => (
              <button key={k} onClick={() => setI(k)} className={`h-1 rounded-full transition-all ${k === i ? 'w-8 bg-brand-primary' : 'w-4 bg-white/15'}`} data-testid={`testimonial-dot-${k}`} aria-label={`go to testimonial ${k+1}`} />
            ))}
          </div>
        </Glass>
      </div>
    </section>
  );
};

// ── 11. LEADERBOARD ───────────────────────────────────────────────────
const Leaderboard = () => {
  const rows = [
    { rank: 1, id: 'AYR-2841', ret: 38.4, status: 'Elite' },
    { rank: 2, id: 'AYR-1024', ret: 31.7, status: 'Elite' },
    { rank: 3, id: 'AYR-7755', ret: 27.2, status: 'Pro' },
    { rank: 4, id: 'AYR-4081', ret: 24.9, status: 'Elite' },
    { rank: 5, id: 'AYR-6633', ret: 22.4, status: 'Pro' },
    { rank: 6, id: 'AYR-2210', ret: 19.8, status: 'Funded' },
    { rank: 7, id: 'AYR-9912', ret: 18.6, status: 'Pro' },
    { rank: 8, id: 'AYR-5544', ret: 17.3, status: 'Funded' },
  ];
  return (
    <section className="relative py-14 md:py-20 bg-gradient-to-b from-black via-[#030303] to-black">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <FadeUp className="text-center mb-12">
          <SectionLabel gold>Top Traders</SectionLabel>
          <h2 className="mt-5 text-4xl md:text-5xl font-black font-heading text-white tracking-[-0.02em]">
            This month&apos;s <span className="text-brand-gold">leaderboard.</span>
          </h2>
        </FadeUp>
        <Glass className="overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/8">
                <th className="text-left p-4 text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Rank</th>
                <th className="text-left p-4 text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Trader</th>
                <th className="text-right p-4 text-[10px] uppercase tracking-wider text-zinc-500 font-bold">30-Day Return</th>
                <th className="text-right p-4 text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.rank} className="border-b border-white/5 last:border-b-0 hover:bg-white/[0.02] transition-colors">
                  <td className="p-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-xs ${r.rank === 1 ? 'bg-brand-gold/15 text-brand-gold border border-brand-gold/30' : r.rank <= 3 ? 'bg-white/5 text-white border border-white/10' : 'text-zinc-500'}`}>
                      {r.rank}
                    </div>
                  </td>
                  <td className="p-4 text-white font-mono">{r.id}</td>
                  <td className="p-4 text-right text-brand-positive font-mono font-bold">+{r.ret}%</td>
                  <td className="p-4 text-right">
                    {r.status === 'Elite' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-brand-gold/10 border border-brand-gold/25 text-brand-gold text-[10px] font-black uppercase tracking-wider">
                        <Crown className="w-3 h-3" /> Elite
                      </span>
                    ) : (
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${r.status === 'Pro' ? 'bg-brand-primary/10 border border-brand-primary/25 text-brand-primary' : 'bg-white/5 border border-white/10 text-zinc-300'}`}>
                        {r.status}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Glass>
      </div>
    </section>
  );
};

// ── 12. FAQ ───────────────────────────────────────────────────────────
const FAQ = () => {
  const faqs = [
    { q: 'What is the Elite Trader Salary Program?', a: 'It’s a monthly salary benefit reserved for top-performing Elite Traders who consistently meet our advancement and consistency criteria. Eligibility requirements apply.' },
    { q: 'How do I become an Elite Trader?', a: 'Pass the challenge → trade as Funded → upgrade to Pro through consistent performance → qualify for Elite. Each tier has specific performance and consistency benchmarks.' },
    { q: 'What does 100% Profit Split mean?', a: 'Elite traders keep 100% of the profits they generate. This is the highest split tier in our progression — earned through consistent professional performance.' },
    { q: 'How does the Consistency Framework work?', a: 'No single trading day should exceed 30% of total profit (2-Step & Instant), and floating losses are monitored. Violating consistency does NOT breach your account — it pauses payouts pending review.' },
    { q: 'How are payouts processed?', a: 'Daily payouts to USDT (TRC20), Indian Bank Account, UPI, or international transfer. Minimum $10. Typical processing within 24 hours of approval.' },
    { q: 'What payment methods are available?', a: 'AYRONN PAY (UPI for India), Crypto (BTC/USDT/ETH via OxaPay), and global bank transfer.' },
    { q: 'Can I scale my account?', a: 'Yes. As you demonstrate consistent performance, you can scale capital allocation through the Pro and Elite tiers.' },
    { q: 'What are the trading rules?', a: 'No HFT, no hedging across multiple accounts, no account sharing, no trading within ±30 min of high-impact news. Clear drawdown and target rules apply.' },
  ];
  const [open, setOpen] = useState(0);
  return (
    <section className="relative py-14 md:py-20">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <FadeUp className="text-center mb-12">
          <SectionLabel>FAQ</SectionLabel>
          <h2 className="mt-5 text-4xl md:text-5xl font-black font-heading text-white tracking-[-0.02em]">
            Everything you wanted to know.
          </h2>
        </FadeUp>
        <Glass className="overflow-hidden divide-y divide-white/5">
          {faqs.map((f, i) => (
            <button key={i} onClick={() => setOpen(open === i ? -1 : i)} data-testid={`faq-${i}`}
              className="w-full text-left p-5 hover:bg-white/[0.02] transition-colors">
              <div className="flex items-start justify-between gap-4">
                <span className="text-white font-semibold text-base">{f.q}</span>
                <ChevronDown className={`w-4 h-4 text-zinc-400 flex-shrink-0 transition-transform mt-1 ${open === i ? 'rotate-180' : ''}`} />
              </div>
              <AnimatePresence>
                {open === i && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} className="overflow-hidden">
                    <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{f.a}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          ))}
        </Glass>
      </div>
    </section>
  );
};

// ── 13. SECURITY + FINAL CTA ──────────────────────────────────────────
const SecurityAndFinalCTA = () => {
  const items = [
    { icon: Lock, label: 'Secure Infrastructure' },
    { icon: FileText, label: 'Transparent Evaluation' },
    { icon: Banknote, label: 'Reliable Payouts' },
    { icon: Smartphone, label: 'Multiple Payment Options' },
    { icon: Users, label: 'Dedicated Support' },
    { icon: TrendingUp, label: 'Performance-Based Advancement' },
  ];
  return (
    <section className="relative py-14 md:py-20 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1200px] h-[600px] bg-brand-primary/8 rounded-full blur-[200px]" />
      </div>
      <div className="relative max-w-5xl mx-auto px-6 lg:px-8">
        <FadeUp className="mb-14">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {items.map((it, i) => (
              <div key={i} className="p-4 rounded-xl bg-white/[0.02] border border-white/5 text-center hover:border-white/15 transition-colors">
                <it.icon className="w-5 h-5 text-brand-primary mx-auto mb-2" strokeWidth={1.5} />
                <div className="text-[10px] text-zinc-300 font-bold uppercase tracking-wider leading-tight">{it.label}</div>
              </div>
            ))}
          </div>
        </FadeUp>

        <FadeUp>
          <div className="text-center">
            <h2 className="text-4xl md:text-6xl font-black font-heading text-white tracking-[-0.02em] leading-[1.05]">
              Ready to start your journey<br />toward <span className="text-brand-gold">Elite Status?</span>
            </h2>
            <p className="mt-5 text-base md:text-lg text-zinc-400 max-w-xl mx-auto">
              Join Ayronn and build your path toward becoming an Elite Trader.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/pricing" data-testid="final-cta-start"
                className="inline-flex items-center gap-2 px-8 py-4 bg-brand-primary text-black font-black rounded-xl hover:scale-[1.02] hover:shadow-[0_0_50px_rgba(0,212,255,0.5)] transition-all">
                Start Challenge <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/pricing" data-testid="final-cta-programs"
                className="inline-flex items-center gap-2 px-8 py-4 text-white font-bold rounded-xl border border-white/15 hover:bg-white/5 hover:border-white/25 transition-all">
                View Programs <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};

// ── PAGE ──────────────────────────────────────────────────────────────
const Home = () => {
  const [plans, setPlans] = useState([]);
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get('/plans').then(r => setPlans(r.data || [])).catch(() => setPlans([]));
    api.get('/stats/public').then(r => setStats(r.data)).catch(() => setStats(null));
  }, []);

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      <AmbientAurora />
      <div className="relative z-10">
        <Navbar />
        <Hero stats={stats} />
        <TrustStrip />
        <WhyChoose />
        <TraderJourney />
        <ChallengePrograms plans={plans} />
        <EliteDesk />
        <EliteCalculator plans={plans} />
        <RuleTransparency />
        <PaymentsAndPayouts />
        <PayoutProofSlideshow />
        <SocialProof />
        <Leaderboard />
        <FAQ />
        <SecurityAndFinalCTA />
        <Footer />
      </div>
      <LivePayoutTicker />
    </div>
  );
};

export default Home;
